import styled from "styled-components";
import { Container } from "react-grid-system";

export const StyledDocumentUploadIcon = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100%;
`;

export const StyledDocumentUploadOptions = styled(Container)`
	& > div {
		margin-bottom: 1rem;

		&:last-child {
			margin: 0;
		}
	}
`;

export const StyledDocumentUploadActions = styled.div`
	display: flex;
	justify-content: flex-end;
	align-items: center;
	column-gap: 4.5rem;
	margin-top: 1.5rem;

	@media (max-width: 768px) {
		column-gap: 1rem;
		flex-direction: column-reverse;
		row-gap: 0.75rem;
		
		button {
			width: 100%;
		}
	}
`;

export const StyledDropzone = styled.div`
	border: 2px dashed #e2e8f0;
	border-radius: 16px;
	padding: 2rem;
	text-align: center;
	transition: all 0.3s ease;
	background: ${props => props.isDragActive ? "#f0fdf4" : "#ffffff"};
	border-color: ${props => props.isDragActive ? "#33AF89" : "#e2e8f0"};
	cursor: pointer;
	
	&:hover {
		border-color: #33AF89;
		background: #f7f8fa;
	}

	.dropzone-content {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 1.5rem;
	}

	.dropzone-icon {
		width: 80px;
		height: 80px;
		opacity: 0.8;
		margin-bottom: 1.5rem;

		@media (max-width: 768px) {
			width: 64px;
			height: 64px;
		}
	}

	.dropzone-text {
		font-size: 1.25rem;
		color: #4D6382;
		margin: 0;
		font-family: "museo, serif";
		font-weight: 600;

		@media (max-width: 768px) {
			font-size: 1.1rem;
		}
	}

	.dropzone-subtext {
		font-size: 1rem;
		color: #4D6382;
		margin: 0;
		font-family: "museo, serif";
		opacity: 0.8;

		@media (max-width: 768px) {
			font-size: 0.9rem;
		}
	}

	@media (max-width: 768px) {
		padding: 1.5rem 1rem;
	}
`;

export const StyledFileList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	margin-top: 1rem;
`;

export const StyledFileItem = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0.75rem 1rem;
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	
	.file-info {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		flex: 1;
	}

	.file-icon {
		width: 24px;
		height: 24px;
		opacity: 0.7;
	}

	.file-details {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
	}

	.file-name {
		font-weight: 500;
		color: #1e293b;
		margin: 0;
	}

	.file-size {
		font-size: 0.85rem;
		color: #64748b;
		margin: 0;
	}

	.file-actions {
		display: flex;
		gap: 0.5rem;
	}
`;

export const StyledDocumentPreview = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	margin-top: 1.5rem;

	.preview-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		border-bottom: 1px solid #e2e8f0;
		padding-bottom: 0.5rem;
	}

	.preview-title {
		font-weight: 600;
		color: #1e293b;
		margin: 0;
	}

	.preview-content {
		display: flex;
		gap: 1.5rem;
	}

	.preview-image {
		flex: 1;
		max-width: 300px;
	}

	.preview-thumbnail {
		width: 100%;
		border-radius: 8px;
		border: 1px solid #e2e8f0;
		object-fit: cover;
	}

	.preview-details {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}
`;

export const StyledConversionPreview = styled.div`
	margin-top: 2rem;
	padding-top: 2rem;
	border-top: 1px solid #e2e8f0;

	.conversion-header {
		margin-bottom: 1.5rem;
	}

	.conversion-title {
		font-weight: 600;
		color: #1e293b;
		margin: 0 0 0.5rem 0;
	}

	.conversion-subtitle {
		color: #64748b;
		font-size: 0.9rem;
		margin: 0;
	}

	.conversion-fields {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.field-group {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.field-label {
		font-weight: 500;
		color: #374151;
		font-size: 0.9rem;
	}

	.field-input {
		padding: 0.75rem 1rem;
		border: 1px solid #d1d5db;
		border-radius: 6px;
		font-size: 0.95rem;
		transition: border-color 0.2s ease;

		&:focus {
			outline: none;
			border-color: #3b82f6;
			box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
		}

		@media (max-width: 768px) {
			font-size: 16px; /* Prevents zoom on iOS */
		}
	}

	.field-textarea {
		padding: 0.75rem 1rem;
		border: 1px solid #d1d5db;
		border-radius: 6px;
		font-size: 0.95rem;
		min-height: 100px;
		resize: vertical;
		font-family: inherit;
		transition: border-color 0.2s ease;

		&:focus {
			outline: none;
			border-color: #3b82f6;
			box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
		}

		@media (max-width: 768px) {
			font-size: 16px; /* Prevents zoom on iOS */
			min-height: 80px;
		}
	}
`;

export const StyledErrorMessage = styled.div`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 1rem 1.5rem;
	background: #fef2f2;
	border: 1px solid #fecaca;
	border-radius: 12px;
	color: #dc2626;
	font-size: 1rem;
	margin-top: 0.75rem;
	font-family: "museo, serif";

	.error-icon {
		width: 18px;
		height: 18px;
		flex-shrink: 0;
	}
`;

export const StyledProgressBar = styled.div`
	width: 100%;
	height: 6px;
	background: #e2e8f0;
	border-radius: 3px;
	overflow: hidden;
	margin-top: 0.5rem;

	.progress-fill {
		height: 100%;
		background: #3b82f6;
		border-radius: 3px;
		transition: width 0.3s ease;
		width: ${props => props.progress || 0}%;
	}
`;